/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
// import CreditCardIcon from '@mui/icons-material/CreditCard';
// import CloseIcon from '@mui/icons-material/Close';
// import ButtonRadio from 'atoms/ButtonRadio/ButtonRadio';
// import { PaymentIcons } from 'molecules/InvoicePopupPaymentOptions/InvoicePopupPaymentOptions.styles';
// import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
// import { ReactComponent as MastercardIcon } from 'molecules/InvoicePopupPaymentOptions/assets/mastercard.svg';
// import { ReactComponent as VisaIcon } from 'assets/icons/visa.svg';
import { useBaoSelector } from 'utils/hooks/redux';
import { usePaymentMethodPolling } from './usePaymentMethodPolling';
import PaymentAuthenticationModal from './PaymentAuthenticationModal';
import { useSnackbar } from 'atoms/Snackbar';

const PAYMENT_METHOD_POLL_KEY = 'pendingPaymentMethodId';
const PAYMENT_METHOD_RETURN_KEY = 'paymentMethodReturnFrom';

type AddPaymentMethodDialogProps = {
  addingPaymentMethod: boolean;
  onAddingPaymentMethod: (addingPaymentMethod: boolean) => void;
};

const AddPaymentMethodDialog = ({
  addingPaymentMethod,
  onAddingPaymentMethod,
}: AddPaymentMethodDialogProps) => {
  const { currentCompany } = useBaoSelector((state) => state.common);
  const { showSnackbar } = useSnackbar();
  const [pendingPaymentMethodId, setPendingPaymentMethodId] = useState<
    string | null
  >(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const { paymentMethod, isPolling, error, stop } = usePaymentMethodPolling({
    paymentMethodId: pendingPaymentMethodId,
    enabled: !!pendingPaymentMethodId,
  });

  // Check if we're returning from bank auth
  useEffect(() => {
    const storedPaymentMethodId = sessionStorage.getItem(
      PAYMENT_METHOD_POLL_KEY
    );
    const returnFrom = sessionStorage.getItem(PAYMENT_METHOD_RETURN_KEY);

    if (storedPaymentMethodId && returnFrom === 'bank-auth') {
      setPendingPaymentMethodId(storedPaymentMethodId);
      setShowAuthModal(true);
      sessionStorage.removeItem(PAYMENT_METHOD_RETURN_KEY);
    }
  }, []);

  // Handle polling completion
  useEffect(() => {
    if (!paymentMethod) return;

    const authStatus = paymentMethod.authentication_status;

    if (authStatus === 'authenticated') {
      showSnackbar('Payment method successfully authenticated!', 'success');
      setShowAuthModal(false);
      sessionStorage.removeItem(PAYMENT_METHOD_POLL_KEY);
      setPendingPaymentMethodId(null);
      onAddingPaymentMethod(false);
    } else if (authStatus === 'failed') {
      showSnackbar('Authentication failed. Please try again.', 'error');
      setShowAuthModal(false);
      sessionStorage.removeItem(PAYMENT_METHOD_POLL_KEY);
      setPendingPaymentMethodId(null);
      onAddingPaymentMethod(false);
    } else if (authStatus === 'declined') {
      showSnackbar('Authentication was declined. Please try again.', 'error');
      setShowAuthModal(false);
      sessionStorage.removeItem(PAYMENT_METHOD_POLL_KEY);
      setPendingPaymentMethodId(null);
      onAddingPaymentMethod(false);
    }
  }, [paymentMethod]);

  // Handle error
  useEffect(() => {
    if (error) {
      showSnackbar(error, 'error');
      setShowAuthModal(false);
      stop();
      sessionStorage.removeItem(PAYMENT_METHOD_POLL_KEY);
      setPendingPaymentMethodId(null);
      onAddingPaymentMethod(false);
    }
  }, [error]);

  const handleAddingPaymentMethod = async () => {
    if (!currentCompany) {
      // eslint-disable-next-line no-console
      console.error('Company not found');
      onAddingPaymentMethod(false);
      return;
    }

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BILLING_SERVICE}/payment/setup-auto-payment`,
        {
          cancel_url: window.location.href,
          success_url: window.location.href,
          company_hs_id: currentCompany?.company_hs_id,
        }
      );

      // Store the payment method ID for polling after return
      if (data.payment_method_id) {
        sessionStorage.setItem(PAYMENT_METHOD_POLL_KEY, data.payment_method_id);
        sessionStorage.setItem(PAYMENT_METHOD_RETURN_KEY, 'bank-auth');
      }

      window.open(data.url, '_self');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error setting up payment method:', err);
      showSnackbar('Failed to setup payment method', 'error');
      onAddingPaymentMethod(false);
    }
  };

  useEffect(() => {
    if (addingPaymentMethod) {
      handleAddingPaymentMethod();
    }
  }, [addingPaymentMethod]);

  const handleAuthModalClose = () => {
    if (!isPolling) {
      setShowAuthModal(false);
      stop();
      sessionStorage.removeItem(PAYMENT_METHOD_POLL_KEY);
      setPendingPaymentMethodId(null);
      onAddingPaymentMethod(false);
    }
  };

  return (
    <>
      <PaymentAuthenticationModal
        open={showAuthModal}
        onClose={handleAuthModalClose}
        isPolling={isPolling}
      />
    </>
  );
};

export default AddPaymentMethodDialog;
